"use client";

import React, { useState } from "react";
import { MapPin, Search, Clock, Navigation, Phone, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const STORES = [{
  id: "1",
  name: "Nepean Central - Bayshore",
  address: "100 Bayshore Dr, Nepean",
  city: "Ottawa",
  distance: "0.8 km",
  status: "Open Now",
  hours: "08:00 - 20:00",
  phone: "613-555-0101",
  lat: 35,
  lng: 45
}, {
  id: "2",
  name: "Greenboro Community Hub",
  address: "363 Lorry Greenberg Dr",
  city: "Ottawa",
  distance: "4.2 km",
  status: "Open Now",
  hours: "09:00 - 21:00",
  phone: "613-555-0102",
  lat: 42,
  lng: 48
}, {
  id: "3",
  name: "Westboro Village Office",
  address: "Richmond Rd & Churchill Ave",
  city: "Ottawa",
  distance: "6.5 km",
  status: "Closing Soon",
  hours: "09:00 - 18:00",
  phone: "613-555-0103",
  lat: 55,
  lng: 50
}, {
  id: "4",
  name: "Bells Corners Service Point",
  address: "Robertson Rd, Nepean",
  city: "Ottawa",
  distance: "9.1 km",
  status: "Closed",
  hours: "10:00 - 17:00",
  phone: "613-555-0104",
  lat: 48,
  lng: 65
}, {
  id: "5",
  name: "Kanata North Tech Park",
  address: "March Rd & Terry Fox Dr",
  city: "Kanata",
  distance: "12.3 km",
  status: "Open Now",
  hours: "08:30 - 19:00",
  phone: "613-555-0105",
  lat: 20,
  lng: 55
}];
export default function LocalAreaNepean() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState<string>(STORES[0].id);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const filteredStores = STORES.filter(store => store.name.toLowerCase().includes(searchQuery.toLowerCase()) || store.city.toLowerCase().includes(searchQuery.toLowerCase()) || store.address.toLowerCase().includes(searchQuery.toLowerCase()));
  const selectedStore = STORES.find(s => s.id === selectedStoreId) || STORES[0];
  return <section data-section-id="264583" id="local-area-nepean" className="bg-background text-foreground py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={`flex flex-col md:flex-row justify-between items-start md:items-end mb-10 gap-6 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
              Serving Nepean & Surrounding Areas
            </h2>
            <p className="text-muted-foreground text-lg font-default">
              From the bustling shops of Bayshore to the quiet streets of Greenboro and Bells Corners, we are your local partner in the Ottawa West region.
            </p>
          </div>
          <Button size="lg" className="shrink-0 rounded-lg px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
            Explore All Areas
          </Button>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 h-[800px] lg:h-[650px] transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-4 flex flex-col h-full bg-card rounded-lg border border-border shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border bg-card/50 backdrop-blur-sm z-10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground size-5" />
                <Input type="text" placeholder="Search neighborhood or street..." className="pl-10 bg-background border-border h-12 rounded-md text-base font-default" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="flex flex-col gap-3 pb-4">
                {filteredStores.length > 0 ? filteredStores.map((store, i) => <Card key={store.id} data-index={i} className={`cursor-pointer transition-all duration-200 border-2 hover:shadow-md rounded-md ${selectedStoreId === store.id ? "border-secondary bg-secondary/10 shadow-sm" : "border-transparent hover:border-secondary/30 bg-background"}`} onClick={() => setSelectedStoreId(store.id)}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-default font-bold text-lg leading-tight text-foreground pr-4">
                            {store.name}
                          </h3>
                          <span className="text-sm font-medium text-muted-foreground whitespace-nowrap font-default">
                            {store.distance}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-1 font-default">
                          {store.address}
                        </p>
                        <div className="flex items-center justify-between">
                          <Badge variant={store.status === "Open Now" ? "default" : store.status === "Closing Soon" ? "secondary" : "outline"} className="rounded-md px-2 py-0.5 text-xs font-semibold font-default bg-primary text-primary-foreground">
                            {store.status}
                          </Badge>
                          <Button variant="ghost" size="icon" className={`size-8 rounded-md ${selectedStoreId === store.id ? "bg-secondary text-secondary-foreground hover:bg-secondary/90" : "text-muted-foreground"}`}>
                            <ChevronRight className="size-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>) : <div className="text-center py-12 px-4">
                    <MapPin className="size-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h4 className="font-default font-semibold text-lg mb-2">No locations found</h4>
                    <p className="text-muted-foreground text-sm mb-6 font-default">
                      We couldn't find any service points matching your search in the Nepean area.
                    </p>
                    <Button variant="outline" className="rounded-md" onClick={() => setSearchQuery("")}>
                      Reset Search
                    </Button>
                  </div>}
              </div>
            </ScrollArea>
          </div>

          {}
          <div className="lg:col-span-8 flex flex-col h-full rounded-lg overflow-hidden border border-border shadow-md relative bg-muted">
            {}
            <div className="absolute inset-0 w-full h-full bg-[#f0f2f0]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_ottawa_map_view.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_ottawa_map_view.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_ottawa_map_view.png-wvc-sizes" alt="Map of Nepean and West Ottawa neighborhoods" className="w-full h-full object-cover opacity-90 mix-blend-multiply" loading="lazy" />
              
              {}
              {filteredStores.map((store, i) => <div key={`pin-${store.id}`} data-index={i} className={`absolute -translate-x-1/2 -translate-y-full transition-all duration-300 z-10 ${selectedStoreId === store.id ? "scale-125 z-20" : "scale-100 hover:scale-110"}`} style={{
              top: `${store.lat}%`,
              left: `${store.lng}%`
            }}>
                  <Button variant="ghost" size="icon" className="p-0 h-auto w-auto hover:bg-transparent" onClick={() => setSelectedStoreId(store.id)} aria-label={`Select ${store.name}`}>
                    <div className="relative flex items-center justify-center">
                      <MapPin className={`size-8 drop-shadow-lg ${selectedStoreId === store.id ? "text-primary fill-primary" : "text-secondary fill-secondary"}`} />
                      {selectedStoreId === store.id && <span className="absolute top-2 size-2 bg-primary-foreground rounded-lg animate-pulse" />}
                    </div>
                  </Button>
                </div>)}
            </div>

            {}
            <div className="absolute bottom-4 left-4 right-4 lg:left-auto lg:right-6 lg:top-6 lg:bottom-auto lg:w-80 z-30">
              <Card className="shadow-xl border-border/50 bg-card/95 backdrop-blur-md rounded-lg">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-default font-bold text-xl leading-tight pr-4 text-foreground">
                      {selectedStore.name}
                    </h3>
                    <Badge variant="secondary" className="shrink-0 font-default bg-secondary text-secondary-foreground">
                      {selectedStore.distance}
                    </Badge>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground font-default">{selectedStore.address}, {selectedStore.city}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Clock className="size-4 text-primary shrink-0" />
                      <span className="text-muted-foreground font-default">
                        <strong className="text-foreground font-medium mr-2">{selectedStore.status}</strong>
                        {selectedStore.hours}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="size-4 text-primary shrink-0" />
                      <span className="text-muted-foreground font-default">{selectedStore.phone}</span>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button className="flex-1 rounded-md gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
                      <Navigation className="size-4" />
                      Get Route
                    </Button>
                    <Button variant="outline" className="flex-1 rounded-md border-primary text-primary hover:bg-primary/5">
                      Contact Us
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}